import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PackagePlus } from 'lucide-react';
import { mapBarcodeError, useTranslation } from '@dukanti/shared';
import type {
  BarcodeKnownVariant,
  BarcodeStockUpdateInput,
  BarcodeStockUpdateResult,
  BarcodeStockUpdateMode,
  TranslationKey,
} from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const MAX_STOCK_RECEIPT_QUANTITY = 999_999;

interface BarcodeStockUpdatePanelProps {
  readonly variant?: BarcodeKnownVariant;
  readonly inventory?: number;
  readonly pending?: boolean;
  readonly isSubmitting?: boolean;
  readonly serviceError?: unknown;
  readonly onSubmit: (
    input: BarcodeStockUpdateInput | { mode: BarcodeStockUpdateMode; quantity: number },
  ) => Promise<BarcodeStockUpdateResult | void> | BarcodeStockUpdateResult | void;
  readonly onSuccess?: (result: BarcodeStockUpdateResult) => void;
  /**
   * F-148 §4.7: quick changes here are not delivery receipts. The hint links to receiving; the
   * receiving routes themselves gate on access (not available / paused). Pass null to hide.
   */
  readonly receivingHref?: string | null;
}

export function BarcodeStockUpdatePanel({
  variant,
  pending = false,
  isSubmitting = false,
  serviceError,
  onSubmit,
  onSuccess,
  receivingHref = '/stock/receiving',
}: BarcodeStockUpdatePanelProps): React.JSX.Element {
  const { t } = useTranslation();
  const [mode, setMode] = useState<BarcodeStockUpdateMode>('add_received');
  const [quantity, setQuantity] = useState('1');
  const [localError, setLocalError] = useState<TranslationKey | null>(null);

  const submit = async () => {
    const parsed = Number(quantity);
    if (!Number.isInteger(parsed)) {
      setLocalError('admin.barcode.stock.errors.wholeNumber');
      return;
    }
    if (parsed < 0) {
      setLocalError('admin.barcode.stock.errors.nonNegative');
      return;
    }
    if (mode === 'add_received' && parsed <= 0) {
      setLocalError('admin.barcode.stock.errors.positiveAdd');
      return;
    }
    if (parsed > MAX_STOCK_RECEIPT_QUANTITY) {
      setLocalError('admin.barcode.stock.errors.maxQuantity');
      return;
    }
    setLocalError(null);
    const result = await onSubmit(
      variant
        ? { variantId: variant.variantId, mode, quantity: parsed }
        : { mode, quantity: parsed },
    );
    if (result) onSuccess?.(result);
  };

  const errorKey =
    localError ?? (serviceError ? (mapBarcodeError(serviceError) as TranslationKey) : null);

  return (
    <section className="border-border bg-card shadow-admin-card rounded-lg border p-4 text-start">
      <h2 className="font-display text-foreground text-lg">
        {t('admin.barcode.result.updateStock')}
      </h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-[auto_1fr_auto] sm:items-end">
        <div
          className="flex rounded-md border p-1"
          role="group"
          aria-label={t('admin.barcode.result.updateStock')}
        >
          {(['add_received', 'set_exact'] as const).map((candidate) => (
            <button
              key={candidate}
              type="button"
              className={`rounded px-3 py-1.5 text-sm ${mode === candidate ? 'bg-admin-lime text-primary-foreground' : 'text-muted-foreground'}`}
              aria-pressed={mode === candidate}
              onClick={() => setMode(candidate)}
              data-testid={`barcode-stock-mode-${candidate.replace('_', '-')}`}
            >
              {t(
                candidate === 'add_received'
                  ? 'admin.barcode.stock.mode.addReceived'
                  : 'admin.barcode.stock.mode.setExact',
              )}
            </button>
          ))}
        </div>
        <div>
          <Label htmlFor="barcode-stock-quantity" className="text-sm">
            {t(
              mode === 'add_received'
                ? 'admin.barcode.stock.quantityLabel'
                : 'admin.barcode.stock.exactLabel',
            )}
          </Label>
          <Input
            id="barcode-stock-quantity"
            type="number"
            min={mode === 'add_received' ? 1 : 0}
            step={1}
            inputMode="numeric"
            dir="ltr"
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
            className="mt-1 text-start"
            data-testid="barcode-stock-quantity"
          />
        </div>
        <Button
          type="button"
          onClick={() => void submit()}
          disabled={isSubmitting || pending}
          data-testid="barcode-stock-submit"
        >
          <PackagePlus className="me-2 size-4" aria-hidden />
          {t('admin.barcode.result.updateStock')}
        </Button>
      </div>
      {errorKey ? (
        <p role="alert" className="text-destructive mt-3 text-start text-sm">
          {t(errorKey, { max: MAX_STOCK_RECEIPT_QUANTITY })}
        </p>
      ) : null}
      <p
        className="text-muted-foreground mt-3 text-start text-xs"
        data-testid="barcode-receiving-hint"
      >
        {t('admin.receiving.barcodeHint.notRecorded')}
        {receivingHref ? (
          <>
            {' '}
            <Link
              to={receivingHref}
              className="text-foreground underline underline-offset-4"
              data-testid="barcode-receiving-hint-link"
            >
              {t('admin.receiving.barcodeHint.openReceiving')}
            </Link>
          </>
        ) : null}
      </p>
    </section>
  );
}
